import type { Distribution } from "@bt/domain/report";
import { useTranslation } from "react-i18next";
import { useWidth } from "./use-width";

/**
 * A distribution at a glance (figures.md › Measures): the middle half as a bar, the median as a
 * tick, p90 and max as marks, the mean faint. With a long tail the axis ends past p90 and the
 * max is written at the edge rather than squeezing the rest. The sentence below says it all in
 * words.
 */
export function DistributionStrip({
  distribution: d,
  format,
  height = 40,
}: {
  distribution: Distribution;
  format: (n: number) => string;
  height?: number;
}) {
  const { t } = useTranslation();
  const [ref, width] = useWidth<HTMLDivElement>();
  const pad = 8;
  const clipped = d.max > 3 * Math.max(d.p90, 1);
  const end = clipped ? d.p90 * 1.3 : Math.max(d.max, 1);
  const x = (v: number) => pad + (Math.min(v, end) / end) * (width - 2 * pad);
  const mid = height / 2;
  const summary = t("adminApp.chart.distribution", {
    median: format(d.median),
    p25: format(d.p25),
    p75: format(d.p75),
    p90: format(d.p90),
    max: format(d.max),
    mean: format(d.mean),
    n: d.n,
    none: d.none,
  });
  return (
    <div ref={ref}>
      {d.n > 0 ? (
        <svg role="img" aria-label={summary} width={width} height={height} className="block">
          <line x1={pad} x2={width - pad} y1={mid} y2={mid} stroke="var(--chart-axis)" />
          <rect
            x={x(d.p25)}
            y={mid - 6}
            width={Math.max(2, x(d.p75) - x(d.p25))}
            height={12}
            rx={4}
            fill="var(--chart-1)"
            opacity={0.35}
          />
          <line
            x1={x(d.p90)}
            x2={x(d.p90)}
            y1={mid - 5}
            y2={mid + 5}
            stroke="var(--muted-foreground)"
            strokeWidth={2}
          />
          <circle
            cx={x(d.mean)}
            cy={mid}
            r={4}
            fill="none"
            stroke="var(--muted-foreground)"
            strokeWidth={1.5}
            opacity={0.7}
          />
          <line
            x1={x(d.median)}
            x2={x(d.median)}
            y1={mid - 10}
            y2={mid + 10}
            stroke="var(--chart-1)"
            strokeWidth={3}
            strokeLinecap="round"
          />
          {clipped ? (
            <text
              x={width - pad}
              y={mid - 10}
              textAnchor="end"
              className="fill-muted-foreground text-[11px] tabular"
            >
              {t("adminApp.chart.maxAt", { max: format(d.max) })} →
            </text>
          ) : (
            <circle
              cx={x(d.max)}
              cy={mid}
              r={4}
              fill="var(--chart-1)"
              stroke="var(--surface)"
              strokeWidth={2}
            />
          )}
        </svg>
      ) : null}
      <p className="mt-1 text-xs text-muted-foreground">
        {d.n > 0 ? summary : t("adminApp.chart.noValues")}
      </p>
    </div>
  );
}
